import { useCallback } from 'react';

import { AppointmentPractitioner } from '@dreryk/egabinet-appointment-frontend-api';

import {
   InteractiveTable,
   InteractiveTableSelectorItemFilter,
   InteractiveTableSelectorItemKeyExtractor,
   InteractiveTableSelectorItemRenderer,
   InteractiveTableSelectorMapper,
   InteractiveTableSelectorTableKeyExtractor,
   Text,
} from '@dreryk/react-components';

import { GroupSessionsFormBean, GroupSessionsFormController } from '../../GroupSessionsForm.types';
import { GroupSessionsPractitionerValue } from '../GroupSessionsPractitionersField.types';
import { SpecialtiesText } from '../../../SpecialtiesText';
import { translationKeys } from '../../../../translations';

type GroupSessionsPractitionersInteractiveTableSelectorProps = Pick<GroupSessionsFormController, 'register'> & {
   readonly practitioners: AppointmentPractitioner[];
};

// eslint-disable-next-line max-lines-per-function
export const GroupSessionsPractitionersInteractiveTableSelector = (
   props: GroupSessionsPractitionersInteractiveTableSelectorProps
): JSX.Element => {
   const mapper = useCallback<InteractiveTableSelectorMapper<AppointmentPractitioner, GroupSessionsPractitionerValue>>(
      (practitioner) => {
         return {
            id: practitioner.id,
            displayName: `${practitioner.firstName.asString()} ${practitioner.lastName.asString()}`,
            specialties: practitioner.specialtyNames,
         };
      },
      []
   );

   const itemRenderer = useCallback<InteractiveTableSelectorItemRenderer<AppointmentPractitioner>>(
      (practitioner, Highlight) => {
         return (
            <span>
               <Highlight
                  node={
                     <Text testId={'fullName'}>
                        {practitioner.firstName.asString()} {practitioner.lastName.asString()}
                     </Text>
                  }
               />
               {', '}
               <Highlight node={<SpecialtiesText specialties={practitioner.specialtyNames} secondary />} />
            </span>
         );
      },
      []
   );

   const itemKeyExtractor = useCallback<InteractiveTableSelectorItemKeyExtractor<AppointmentPractitioner>>(
      (practitioner) => {
         return practitioner.id.toString();
      },
      []
   );

   const tableKeyExtractor = useCallback<InteractiveTableSelectorTableKeyExtractor<GroupSessionsPractitionerValue>>(
      (value) => {
         return value.id.toString();
      },
      []
   );

   const itemFilter = useCallback<InteractiveTableSelectorItemFilter<AppointmentPractitioner>>((item) => {
      return [item.firstName.asString(), item.lastName.asString(), ...item.specialtyNames];
   }, []);

   return (
      <InteractiveTable.Selector<AppointmentPractitioner, GroupSessionsPractitionerValue, GroupSessionsFormBean>
         register={props.register}
         testId={'practitionersSelector'}
         name={'practitioners.selector'}
         label={translationKeys.assignPractitioner}
         placeholder={translationKeys.practitionersDeclensionMap}
         items={props.practitioners}
         itemKeyExtractor={itemKeyExtractor}
         tableKeyExtractor={tableKeyExtractor}
         itemRenderer={itemRenderer}
         itemFilter={itemFilter}
         mapper={mapper}
         required
      />
   );
};
